import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, StoreIcon, XIcon } from 'lucide-react';
import { BackLink, Button, CardHeader, EmptyState, ErrorNotice, Field, ImageUploadField, LoadingState, PageEyebrow, Panel, TextArea, TextInput } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { errorMessage, uploadMedia } from '@/lib/api';
import { createSupplyItem, readSupplyItem, updateSupplyItem, type SupplyItem, type SupplyItemWrite, type SupplySource } from '@/lib/supplies';

const SUPPLIES_LIST = '/settings?tab=supplies';

/** One source row as edited; the id keeps React keys stable while rows come and go. */
interface SourceDraft {
  readonly id: number;
  readonly vendor: string;
  readonly url: string;
  readonly sku: string;
  readonly note: string;
}
let nextSourceId = 1;

function draftFrom(source: SupplySource): SourceDraft {
  return { id: nextSourceId++, vendor: source.vendor, url: source.url ?? '', sku: source.sku ?? '', note: source.note ?? '' };
}
function blankSource(): SourceDraft {
  return { id: nextSourceId++, vendor: '', url: '', sku: '', note: '' };
}
function sourceFrom(draft: SourceDraft): SupplySource {
  return {
    vendor: draft.vendor.trim(),
    ...(draft.url.trim() ? { url: draft.url.trim() } : {}),
    ...(draft.sku.trim() ? { sku: draft.sku.trim() } : {}),
    ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
  };
}

/**
 * Settings → Supplies → one item, as a page: what it is, what it looks
 * like, and every place it can be bought. New at /settings/supplies/new,
 * existing at /settings/supplies/:itemId. Internal roles.
 */
export function SupplyItemPage(): React.ReactElement {
  const { itemId } = useParams<{ itemId: string }>();
  const isNew = itemId === undefined;
  const item = useQuery({ queryKey: ['supply-item', itemId], queryFn: ({ signal }) => readSupplyItem(itemId ?? '', signal), enabled: !isNew });

  if (!isNew && item.isPending) return <LoadingState label="Loading supply item…" />;
  if (!isNew && item.isError) return <ErrorNotice title="Supply item unavailable" message={errorMessage(item.error)} />;

  return (
    <section className="animate-portal-rise">
      <BackLink to={SUPPLIES_LIST}>All supplies</BackLink>
      <header className="mt-4">
        <PageEyebrow>SUPPLY ITEM</PageEyebrow>
        <h1 className="text-portal-page tracking-[-1.7px]">{item.data ? item.data.name : 'New supply item'}</h1>
        <p className="mt-1 text-portal-small text-portal-muted">
          {item.data ? 'Program kits draw from this item; packing lists already built keep their own copy.' : 'Something the warehouse packs, with a picture and every place it can be bought.'}
        </p>
      </header>
      <SupplyItemForm key={item.data?._id ?? 'new'} {...(item.data ? { existing: item.data } : {})} />
    </section>
  );
}

function SupplyItemForm({ existing }: { readonly existing?: SupplyItem }): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [imageUrl, setImageUrl] = useState(existing?.imageUrl ?? '');
  const [unit, setUnit] = useState(existing?.unit ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [isActive, setIsActive] = useState(existing?.isActive ?? true);
  const [sources, setSources] = useState<readonly SourceDraft[]>((existing?.sources ?? []).map(draftFrom));

  const save = useMutation({
    mutationFn: () => {
      const body: SupplyItemWrite = {
        name: name.trim(),
        description: description.trim(),
        imageUrl: imageUrl.trim(),
        unit: unit.trim(),
        notes: notes.trim(),
        isActive,
        sources: sources.map(sourceFrom),
      };
      return existing ? updateSupplyItem(existing._id, body) : createSupplyItem(body);
    },
    onSuccess: async (saved) => {
      toast.success(existing ? `Saved ${saved.name}` : `Added ${saved.name}`);
      await queryClient.invalidateQueries({ queryKey: ['supplies'] });
      await queryClient.invalidateQueries({ queryKey: ['supply-item', saved._id] });
      navigate(SUPPLIES_LIST);
    },
  });
  function updateSource(id: number, patch: Partial<SourceDraft>): void {
    setSources((current) => current.map((source) => (source.id === id ? { ...source, ...patch } : source)));
  }
  const sourcesValid = sources.every((source) => source.vendor.trim().length > 0);
  const valid = name.trim().length > 0 && sourcesValid && !save.isPending;

  return (
    <form
      className="mt-6 space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <Panel className="p-5">
        <CardHeader title="Item" description="What it is and how it is counted." />
        <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
          <Field guide="supply-name" label="Name" htmlFor="supply-name" required hint="As the warehouse and facilitators know it.">
            <TextInput id="supply-name" value={name} maxLength={160} placeholder="Collapsible canvas bins (green)" onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field guide="supply-unit" label="Unit" htmlFor="supply-unit" hint="Sets, boxes, each.">
            <TextInput id="supply-unit" value={unit} maxLength={30} onChange={(event) => setUnit(event.target.value)} />
          </Field>
        </div>
        <div className="mt-4">
          <Field guide="supply-description" label="Description" htmlFor="supply-description" hint="What it is and what it is for.">
            <TextArea id="supply-description" rows={2} value={description} maxLength={2000} onChange={(event) => setDescription(event.target.value)} />
          </Field>
        </div>
        <div className="mt-4">
          <ImageUploadField guide="supply-picture" label="Picture" description="What the facilitator should look for in the box." value={imageUrl} onChange={setImageUrl} upload={async (file) => (await uploadMedia(file, { kind: 'supply' })).url} />
        </div>
        <div className="mt-4">
          <Field guide="supply-notes" label="Notes" htmlFor="supply-notes" hint="Packing quirks, sizes, what to check before it ships.">
            <TextArea id="supply-notes" rows={2} value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} />
          </Field>
        </div>
        <label data-guide="supply-active" className="mt-4 flex items-center gap-2 text-portal-small">
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          Active (offered in kits)
        </label>
      </Panel>

      <Panel className="p-5">
        <CardHeader
          guide="supply-sources"
          title="Where to order"
          description="Every place this item can be bought. The first is the usual one."
          aside={
            sources.length > 0 ? (
              <Button guide="supply-add-source" type="button" variant="secondary" size="compact" onClick={() => setSources((current) => [...current, blankSource()])}>
                <PlusIcon aria-hidden className="size-4" /> Add source
              </Button>
            ) : undefined
          }
        />
        {sources.length === 0 ? (
          <EmptyState
            icon={<StoreIcon aria-hidden className="size-6" />}
            title="No sources yet"
            message="Add each supplier this item can be reordered from, with the product page and part number."
            action={
              <Button guide="supply-add-source" type="button" onClick={() => setSources([blankSource()])}>
                Add source
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {sources.map((source, index) => (
              <div key={source.id} className="rounded-portal-button border border-portal-line p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">{index === 0 ? 'Usual source' : `Source ${index + 1}`}</span>
                  <button type="button" aria-label={`Remove source ${index + 1}`} onClick={() => setSources((current) => current.filter((row) => row.id !== source.id))} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-rose-600">
                    <XIcon aria-hidden className="size-4" />
                  </button>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field guide="supply-source-vendor" label="Supplier" htmlFor={`source-vendor-${source.id}`} required hint="Who we buy it from.">
                    <TextInput id={`source-vendor-${source.id}`} value={source.vendor} maxLength={120} placeholder="Amazon" onChange={(event) => updateSource(source.id, { vendor: event.target.value })} />
                  </Field>
                  <Field guide="supply-source-url" label="Product page" htmlFor={`source-url-${source.id}`} hint="The link, so a reorder is one click.">
                    <TextInput id={`source-url-${source.id}`} type="url" value={source.url} placeholder="https://" onChange={(event) => updateSource(source.id, { url: event.target.value })} />
                  </Field>
                  <Field guide="supply-source-sku" label="Part number" htmlFor={`source-sku-${source.id}`} hint="Their SKU or item code, if they use one.">
                    <TextInput id={`source-sku-${source.id}`} value={source.sku} maxLength={80} onChange={(event) => updateSource(source.id, { sku: event.target.value })} />
                  </Field>
                  <Field guide="supply-source-note" label="Note" htmlFor={`source-note-${source.id}`} hint="Lead time, minimum order, who to call.">
                    <TextInput id={`source-note-${source.id}`} value={source.note} maxLength={300} onChange={(event) => updateSource(source.id, { note: event.target.value })} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
      <div className="flex items-center justify-end gap-3">
        <Button type="button" variant="secondary" to={SUPPLIES_LIST} disabled={save.isPending}>
          Cancel
        </Button>
        <Button guide="supply-save" type="submit" disabled={!valid}>
          {save.isPending ? 'Saving…' : existing ? 'Save item' : 'Add item'}
        </Button>
      </div>
    </form>
  );
}
